/**
 * 🏭 KioskEntrepot.jsx — Mode Kiosque Entrepôt
 *
 * URL : /kiosk/entrepot
 *
 * Conçu pour l'ordinateur partagé de l'entrepôt. Permet à n'importe quel
 * employé de s'authentifier rapidement avec son numero_employe + mot de passe
 * personnel, accéder UNIQUEMENT à la page Entrepôt, et se déconnecter en un
 * clic. Auto-logout après 5 min d'inactivité.
 *
 * Flow :
 *   1. Écran de login (numero + password)
 *   2. POST /api/kiosk/auth → reçoit token + employee
 *   3. Stocke token+user dans localStorage (clés standards : auth_token, user)
 *      + marqueur `kiosk_mode=entrepot`
 *   4. Redirection vers /entrepot?kiosk=1 (sans sidebar)
 *   5. Auto-déconnexion après 5 min d'inactivité (timer reset à chaque
 *      clic/keypress/touch) — purge le localStorage et retour au login.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import BrandedAuthLayout from '@/components/BrandedAuthLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const INACTIVITY_MS = 5 * 60 * 1000; // 5 minutes

export default function KioskEntrepot() {
  const navigate = useNavigate();
  const location = useLocation();
  const [numero, setNumero] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const numeroRef = useRef(null);

  // 🆕 Auto-focus sur le champ numéro à l'arrivée
  useEffect(() => {
    if (numeroRef.current) numeroRef.current.focus();
  }, []);

  // Si on arrive ici alors qu'une session kiosque est active, purge + reset
  useEffect(() => {
    const kioskMarker = localStorage.getItem('kiosk_mode');
    if (kioskMarker === 'entrepot') {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      localStorage.removeItem('kiosk_mode');
      localStorage.removeItem('kiosk_employee');
    }
  }, []);

  const handleLogin = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!numero.trim() || !password) {
      setError('Numéro d\'employé et mot de passe requis');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/kiosk/auth`, {
        numero_employe: numero.trim(),
        password,
      });
      const { token, employee } = res.data;
      // Stocker la session sous les clés standards (intercepteur axios envoie auth_token)
      localStorage.setItem('auth_token', token);
      // Construire un user minimal compatible avec useAuth
      const user = {
        id: employee.id,
        employee_id: employee.id,
        prenom: employee.prenom,
        nom: employee.nom,
        nom_complet: employee.nom_complet,
        // 🆕 [Phase 18] On stocke le rôle effectif (proprio/peintre/installateur/...)
        // afin que KioskScanMode adapte l'UI de liaison obligatoire.
        role: employee.role || 'employee',
        role_terrain: employee.role_terrain || null,
        numero_employe: employee.numero_employe,
        photo_url: employee.photo_url,
      };
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('kiosk_mode', 'entrepot');
      localStorage.setItem('kiosk_employee', JSON.stringify(employee));
      localStorage.setItem('kiosk_started_at', String(Date.now()));
      toast.success(`Bonjour ${employee.prenom} !`);
      // Rediriger en plein écran sur /entrepot avec flag kiosk
      window.location.href = '/entrepot?kiosk=1';
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Connexion échouée';
      setError(typeof msg === 'string' ? msg : 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <BrandedAuthLayout
      title="Mode Kiosque Entrepôt"
      subtitle="Entrez votre numéro d'employé deux fois pour ouvrir votre session."
      dataTestid="kiosk-login-card"
      footerNote={(
        <span className="inline-flex items-center justify-center gap-1.5 text-amber-300/80 text-[11px] uppercase tracking-wider">
          <ShieldCheck className="w-3.5 h-3.5" />
          Session limitée à l'Entrepôt — Déconnexion auto après 5 min d'inactivité
        </span>
      )}
    >
      <form onSubmit={handleLogin} className="space-y-4">
        <div>
          <Label htmlFor="numero" className="text-sm font-medium text-slate-200">
            Numéro d'employé
          </Label>
          <Input
            ref={numeroRef}
            id="numero"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={numero}
            onChange={(e) => setNumero(e.target.value)}
            placeholder="Ex: 1234"
            className="mt-1 text-lg h-12 bg-white"
            data-testid="kiosk-numero-input"
            disabled={loading}
          />
        </div>
        <div>
          <Label htmlFor="password" className="text-sm font-medium text-slate-200">
            Confirmation — entrez à nouveau votre N° d'employé
          </Label>
          <Input
            id="password"
            type="password"
            autoComplete="off"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Le même N° qu'au-dessus"
            className="mt-1 text-lg h-12 bg-white"
            data-testid="kiosk-password-input"
            disabled={loading}
          />
          <p className="mt-1 text-xs text-amber-200/70 italic">
            ℹ Mode Kiosque : pas de mot de passe personnel, on saisit deux fois le numéro pour confirmer.
          </p>
        </div>

        {error && (
          <div
            className="bg-red-500/15 border border-red-400/40 text-red-200 text-sm rounded-lg p-3"
            data-testid="kiosk-error"
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          disabled={loading}
          className="w-full h-12 text-base font-semibold bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
          data-testid="kiosk-submit-btn"
        >
          {loading ? (
            <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Connexion...</>
          ) : (
            <><User className="w-5 h-5 mr-2" /> Se connecter</>
          )}
        </Button>
      </form>
    </BrandedAuthLayout>
  );
}
